import type { Seat, SeatCategory } from '@cineamo/ui/src/seat-map/typedefs';

import { recommendBestSeats } from './best-seats.ts';

// Only type imports besides best-seats, so node --test can load it without an install.

/** A category is worth showing the guest when the cinema gave it a photo, a sentence or benefits. */
export function hasShowcase(category: SeatCategory | undefined | null): category is SeatCategory {
    return (
        !!category && (!!category.imageUrl || !!category.description?.trim() || (category.benefits?.length ?? 0) > 0)
    );
}

/** The categories the room's seats actually have; the cinema's other categories are none of this room's business. */
export function roomSeatCategories(seats: Seat[]): SeatCategory[] {
    const byId = new Map<SeatCategory['id'], SeatCategory>();
    for (const seat of seats) {
        if (seat.category && !byId.has(seat.category.id)) byId.set(seat.category.id, seat.category);
    }
    return [...byId.values()];
}

/** The price options a category's default price is read from; a structural subset of PriceOptionDto. */
type DefaultPriceSource = {
    isShowingDefault?: boolean;
    priceTotal?: number | null;
    _embedded?: { seatCategory?: { id: SeatCategory['id'] } };
};

/** What a seat of each category costs on the showing-default price option, in cents: what the guest actually pays. */
export function defaultPricesByCategory(options: DefaultPriceSource[]): Map<SeatCategory['id'], number> {
    const prices = new Map<SeatCategory['id'], number>();
    for (const option of options) {
        const id = option._embedded?.seatCategory?.id;
        if (option.isShowingDefault && id !== undefined && typeof option.priceTotal === 'number') {
            prices.set(id, option.priceTotal);
        }
    }
    return prices;
}

/** Extra cents per seat over the room's cheapest category; undefined when the category costs no extra or is unpriced. */
export function categorySurcharge(
    category: SeatCategory,
    roomCategories: SeatCategory[],
    prices: Map<SeatCategory['id'], number>
): number | undefined {
    const own = prices.get(category.id);
    const cheapest = Math.min(...roomCategories.map((c) => prices.get(c.id) ?? Infinity));
    if (own === undefined || !Number.isFinite(cheapest) || own <= cheapest) return undefined;
    return own - cheapest;
}

export type SeatUpsell = {
    category: SeatCategory;
    /** Extra cents per seat. */
    priceDifference: number;
    /** A free group of the target category, as large as the current selection. */
    seats: Seat[];
};

/** What the cart knows about the selection's prices: a swap puts every seat on its new category's default option. */
export type UpsellPricing = {
    /** The guest turned the offer down, or undid a swap: nothing is offered again. */
    isDeclined?: boolean;
    selected: { id: Seat['id']; priceOptionId?: string | number }[];
    /** The showing-default price option of a category, if it has one. */
    defaultPriceOptionId: (categoryId: SeatCategory['id']) => string | number | undefined;
    /** The price of a category's default option in cents (see `defaultPricesByCategory`). */
    defaultPrice: (categoryId: SeatCategory['id']) => number | undefined;
};

/** Only plain seats move: loveseats, beds, wheelchair places and the like are not interchangeable. */
const isStandardSeat = (seat: Seat) => (seat.type as string) === 'seat';

/**
 * The better category to offer for the current selection, or null. `seats` carry their lock and selection state.
 * Offered only for a selection of plain seats, all in one category and on their default price option; the target
 * is the cheapest category by default price strictly above the current one that has a showcase and a free group
 * for the whole party. With `targetCategoryId` only that category is considered (the modal's upgrade button). The difference is that of the default prices the guest pays, never `priceChange`.
 */
export function pickSeatUpsell(
    seats: Seat[],
    pricing: UpsellPricing,
    targetCategoryId?: SeatCategory['id']
): SeatUpsell | null {
    const selected = seats.filter((seat) => seat.isSelected);
    if (pricing.isDeclined || selected.length === 0) return null;
    const current = selected[0].category;
    if (!current || !selected.every((seat) => isStandardSeat(seat) && seat.category?.id === current.id)) return null;
    const isDefaultPrice = (seat: Seat) =>
        pricing.selected.find((s) => s.id === seat.id)?.priceOptionId === pricing.defaultPriceOptionId(current.id);
    if (!selected.every(isDefaultPrice)) return null;

    const currentPrice = pricing.defaultPrice(current.id);
    if (currentPrice === undefined) return null;
    // The cheapest step up first: the one guests take. A tier without a free group for the party falls through.
    const candidates = roomSeatCategories(seats)
        .filter(
            (category) =>
                (targetCategoryId === undefined || category.id === targetCategoryId) &&
                (pricing.defaultPrice(category.id) ?? -Infinity) > currentPrice &&
                hasShowcase(category) &&
                pricing.defaultPriceOptionId(category.id) !== undefined
        )
        .sort((a, b) => pricing.defaultPrice(a.id)! - pricing.defaultPrice(b.id)!);
    for (const target of candidates) {
        const group = recommendBestSeats(
            seats.filter((seat) => seat.category?.id === target.id && isStandardSeat(seat)),
            selected.length
        );
        if (group.length === selected.length) {
            return { category: target, priceDifference: pricing.defaultPrice(target.id)! - currentPrice, seats: group };
        }
    }
    return null;
}

/** "1–2" for neighbors, "1, 3" otherwise; per row, in map order. */
export function describeSeats(seats: Seat[]): { row: string; positions: string }[] {
    const byRow = new Map<string, number[]>();
    for (const seat of seats) {
        const row = String(seat.rowName ?? '');
        byRow.set(row, [...(byRow.get(row) ?? []), Number(seat.positionName)]);
    }
    return [...byRow].map(([row, numbers]) => {
        const sorted = numbers.sort((a, b) => a - b);
        const isRange = sorted.length > 1 && sorted.every((n, i) => i === 0 || n === sorted[i - 1] + 1);
        return { row, positions: isRange ? `${sorted[0]}\u2013${sorted.at(-1)}` : sorted.join(', ') };
    });
}
